'use client';

import { ReactNode, createContext, useContext, useState } from 'react';
import styles from '@/styles/components/accordion.module.scss';
import { Box } from '@/components/layout/box';
import ChevronIcon from '@/assets/svg/icons/chevron';
import classNames from 'classnames';

interface AccordionContextProps {
  isOpen: boolean;
  toggleOpen: () => void;
}

const AccordionContext = createContext<AccordionContextProps | undefined>(undefined);

interface AccordionProps {
  children: ReactNode;
  defaultOpen?: boolean;
}
export default function Accordion({ children, defaultOpen = false }: AccordionProps) {
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  function toggleOpen() {
    setIsOpen(!isOpen);
  }

  return (
    <AccordionContext.Provider value={{ isOpen, toggleOpen }}>
      <div className={styles.accordion}>{children}</div>
    </AccordionContext.Provider>
  );
}

interface AccordionItemProps {
  children: ReactNode;
}
Accordion.Item = function AccordionItem({ children }: AccordionItemProps) {
  return <div>{children}</div>;
};

interface AccordionHeaderProps {
  children: ReactNode;
}
Accordion.Header = function AccordionHeader({ children }: AccordionHeaderProps) {
  const context = useContext(AccordionContext);

  if (!context) {
    throw new Error('AccordionHeader must be used within an Accordion');
  }

  const { isOpen, toggleOpen } = context;

  return (
    <div
      className={styles.header}
      onClick={toggleOpen}
      role='button'
      tabIndex={0}
      aria-expanded={isOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleOpen();
        }
      }}
    >
      <Box>{children}</Box>
      <Box className={styles.chevron}>
        <ChevronIcon direction={isOpen ? 'up' : 'down'} />
      </Box>
    </div>
  );
};

interface AccordionContentProps {
  children: ReactNode;
}
Accordion.Content = function AccordionContent({ children }: AccordionContentProps) {
  const context = useContext(AccordionContext);

  if (!context) {
    throw new Error('AccordionContent must be used within an Accordion');
  }

  const { isOpen } = context;

  return (
    <div className={classNames(styles.content, { [styles.open]: isOpen })}>
      <div className={styles.contentInner}>{children}</div>
    </div>
  );
};
